import { Pipe, PipeTransform, OnDestroy } from '@angular/core';
import { IQBoostViewStore } from '@maximizer/ai/data-access';
import { EntityEvent, EntityType, EntryType, InteractionTimelineEvent, InteractionType } from '@maximizer/core/shared/domain';
import { OpenEntityArgs } from '@maximizer/core/shared/ui/config';
import { AiMarkupUtils } from '../classes/ai-markup-utils';
import { TranslateService } from '@ngx-translate/core';
import {
  DATA_ATTRIBUTES,
  ENTITY_KEY_PARAM,
  REFERENCE_LINK_CLASS,
  createReferenceLink,
} from './ref-link.utils';

const REFERENCE_LINK_IGNORED_TYPE_FRAGMENTS: readonly string[] = [
  'datahub_insurance',
  'datahub_investments',
  'insurance',
  'investments',
];

function shouldIgnoreReferenceType(type: string | undefined): boolean {
  if (!type) return false;
  return REFERENCE_LINK_IGNORED_TYPE_FRAGMENTS
    .some((fragment) => type.includes(fragment));
}

@Pipe({ name: 'clickableRef', })
export class ClickableRefPipe implements PipeTransform, OnDestroy {
  private clickHandler: ((event: MouseEvent) => void) | null = null;
  private readonly onFormatTemplate = '';

  constructor(
    private readonly store: IQBoostViewStore,
    private readonly translate: TranslateService,
  ) {
    this.setupEventHandlers();
    this.onFormatTemplate = this.translate.instant('ai.references.onFormat');
  }

  transform(value?: string | null): string {
    if (!value) return '';

    return AiMarkupUtils.replaceMaximizerReferences(value, (params) => {
      const exists = params[ENTITY_KEY_PARAM] && params['type'];
      const type = params['type']?.toLowerCase();

      if (exists) {
        if (shouldIgnoreReferenceType(type)) {
          return null;
        }
        return createReferenceLink(params, this.onFormatTemplate, this.translate);
      }

      return null
    });
  }

  ngOnDestroy() {
    this.removeEventHandlers();
  }

  private setupEventHandlers(): void {
    this.removeEventHandlers();
    this.clickHandler = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target?.classList.contains(REFERENCE_LINK_CLASS)) return;

      event.preventDefault();
      const entity = this.createEntityFromTarget(target);
      if (entity) {
        this.store.triggerOpenEntity(new OpenEntityArgs(entity));
      }
    };

    document.addEventListener('click', this.clickHandler);
  }

  private createEntityFromTarget(target: HTMLElement): EntityEvent | null {
    const key = target.getAttribute(DATA_ATTRIBUTES.KEY);
    const type = target.getAttribute(DATA_ATTRIBUTES.TYPE);
    if (!key || !type) return null;

    const entity: EntityEvent = {
      key,
      type: type.toLowerCase() as EntityType,
    };

    const withId = target.getAttribute(DATA_ATTRIBUTES.WITH_ID);
    const withType = target.getAttribute(DATA_ATTRIBUTES.WITH_TYPE);
    if (withId && withType) {
      const interactionDirection = target.getAttribute(DATA_ATTRIBUTES.INTERACTION_DIRECTION);
      const interactionType = target.getAttribute(DATA_ATTRIBUTES.INTERACTION_TYPE);
      const interactionExternal = target.getAttribute(DATA_ATTRIBUTES.INTERACTION_EXTERNAL);

      entity.with = {
        id: withId,
        type: withType as EntryType,
        display: '',
        isNew: false,
      };

      if (interactionDirection && interactionType && interactionExternal) {
        (entity as InteractionTimelineEvent).interaction = {
          direction: interactionDirection,
          type: interactionType,
          external: interactionExternal.toLowerCase() === 'true',
          icon: 0,
          display: '',
        } as InteractionType;
      }
    }

    return entity;
  }

  private removeEventHandlers(): void {
    if (this.clickHandler) {
      document.removeEventListener('click', this.clickHandler);
      this.clickHandler = null;
    }
  }
}
